/**
 * CAD Integration Orchestrator
 * Coordinates all CAD modules and ensures they work together seamlessly
 */

class CADIntegration {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.modules = {};
    this.initialized = false;
  }

  /**
   * Initialize all CAD modules
   */
  async initializeAllModules() {
    console.log('[CADIntegration] Initializing all CAD modules...');

    try {
      // Initialize core modules
      if (typeof AdvancedPrimitives !== 'undefined') {
        this.modules.advancedPrimitives = new AdvancedPrimitives();
        this.wireUpAdvancedPrimitives();
        console.log('[CADIntegration] ✓ Advanced Primitives loaded');
      }

      if (typeof CADKeyboardShortcuts !== 'undefined') {
        this.modules.keyboardShortcuts = new CADKeyboardShortcuts(this.cadViewer);
        console.log('[CADIntegration] ✓ Keyboard Shortcuts loaded');
      }

      if (typeof AutoSaveManager !== 'undefined') {
        this.modules.autoSave = new AutoSaveManager(this.cadViewer);
        console.log('[CADIntegration] ✓ Auto-Save loaded');
      }

      if (typeof CADUIEnhancements !== 'undefined') {
        this.modules.uiEnhancements = new CADUIEnhancements(this.cadViewer);
        this.cadViewer.uiEnhancements = this.modules.uiEnhancements;
        console.log('[CADIntegration] ✓ UI Enhancements loaded');
      }

      if (typeof EnhancedExtrude !== 'undefined') {
        this.modules.enhancedExtrude = new EnhancedExtrude(this.cadViewer);
        this.cadViewer.enhancedExtrude = this.modules.enhancedExtrude;
        console.log('[CADIntegration] ✓ Enhanced Extrude loaded');
      }

      // Initialize agent-created modules
      if (typeof SurfaceModeler !== 'undefined') {
        this.modules.surfaceModeler = new SurfaceModeler(this.cadViewer);
        this.cadViewer.surfaceModeler = this.modules.surfaceModeler;
        console.log('[CADIntegration] ✓ Surface Modeler loaded');
      }

      if (typeof GearDesigner !== 'undefined') {
        this.modules.gearDesigner = new GearDesigner(this.cadViewer);
        this.cadViewer.gearDesigner = this.modules.gearDesigner;
        console.log('[CADIntegration] ✓ Gear Designer loaded');
      }

      if (typeof SheetMetalWorkbench !== 'undefined') {
        this.modules.sheetMetal = new SheetMetalWorkbench(this.cadViewer);
        this.cadViewer.sheetMetal = this.modules.sheetMetal;
        console.log('[CADIntegration] ✓ Sheet Metal loaded');
      }

      if (typeof HistoryManager !== 'undefined') {
        this.modules.historyManager = new HistoryManager(this.cadViewer);
        this.cadViewer.historyManager = this.modules.historyManager;
        console.log('[CADIntegration] ✓ History Manager (Undo/Redo) loaded');
      }

      if (typeof ProfileDetector !== 'undefined') {
        this.modules.profileDetector = new ProfileDetector();
        this.cadViewer.profileDetector = this.modules.profileDetector;
        console.log('[CADIntegration] ✓ Profile Detector loaded');
      }

      this.initialized = true;
      console.log('[CADIntegration] ✅ All modules initialized successfully');

      Utils.notify('CAD system ready - All features loaded', 'success');

      return true;
    } catch (error) {
      console.error('[CADIntegration] Initialization error:', error);
      Utils.notify('Some CAD features may not be available', 'warning');
      return false;
    }
  }

  /**
   * Wire up advanced primitives buttons
   */
  wireUpAdvancedPrimitives() {
    const primitives = this.modules.advancedPrimitives;

    document.getElementById('cadPrimHelix')?.addEventListener('click', () => {
      this.createAdvancedPrimitive('helix');
    });

    document.getElementById('cadPrimPrism')?.addEventListener('click', () => {
      this.createAdvancedPrimitive('prism');
    });

    document.getElementById('cadPrimEllipsoid')?.addEventListener('click', () => {
      this.createAdvancedPrimitive('ellipsoid');
    });
  }

  /**
   * Create advanced primitive
   */
  createAdvancedPrimitive(type) {
    const primitives = this.modules.advancedPrimitives;
    if (!primitives) return;

    let geometry, parameters;

    switch (type) {
      case 'helix':
        const radius = parseFloat(prompt('Helix radius:', '0.5'));
        const pitch = parseFloat(prompt('Pitch:', '0.2'));
        const height = parseFloat(prompt('Height:', '2.0'));
        const turns = height / pitch;

        if (isNaN(radius) || isNaN(pitch) || isNaN(height)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = primitives.createHelix(radius, pitch, height, turns);
        parameters = { radius, pitch, height, turns };
        break;

      case 'prism':
        const prismRadius = parseFloat(prompt('Radius:', '0.5'));
        const prismHeight = parseFloat(prompt('Height:', '1.0'));
        const sides = parseInt(prompt('Number of sides:', '6'));

        if (isNaN(prismRadius) || isNaN(prismHeight) || isNaN(sides)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = primitives.createPrism(prismRadius, prismHeight, sides);
        parameters = { radius: prismRadius, height: prismHeight, sides };
        break;

      case 'ellipsoid':
        const rx = parseFloat(prompt('Radius X:', '1.0'));
        const ry = parseFloat(prompt('Radius Y:', '0.7'));
        const rz = parseFloat(prompt('Radius Z:', '0.5'));

        if (isNaN(rx) || isNaN(ry) || isNaN(rz)) {
          Utils.notify('Invalid parameters', 'error');
          return;
        }

        geometry = primitives.createEllipsoid(rx, ry, rz);
        parameters = { radiusX: rx, radiusY: ry, radiusZ: rz };
        break;

      default:
        return;
    }

    // Create feature
    const feature = new Feature(
      Utils.generateId(),
      'primitive',
      {
        name: `${type.charAt(0).toUpperCase() + type.slice(1)}${this.cadViewer.document.features.length + 1}`,
        primitiveType: type,
        ...parameters
      }
    );

    this.cadViewer.document.addFeature(feature);
    this.cadViewer.updateFeatureTree();

    // Create mesh
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    this.cadViewer.scene.add(mesh);
    this.cadViewer.cadObjects.set(feature.id, mesh);

    Utils.notify(`Created ${type}`, 'success');
  }

  /**
   * Check system health
   */
  async runHealthCheck() {
    console.log('[CADIntegration] Running health check...');

    const health = {
      modules: {},
      geometryKernel: this.cadViewer.isReady(),
      document: this.cadViewer.document !== null,
      features: this.cadViewer.document?.features.length || 0,
      agents: {}
    };

    // Check each module
    for (const [name, module] of Object.entries(this.modules)) {
      health.modules[name] = module !== null;
    }

    console.log('[CADIntegration] Health check:', health);

    return health;
  }

  /**
   * Get integration status
   */
  getStatus() {
    return {
      initialized: this.initialized,
      moduleCount: Object.keys(this.modules).length,
      modules: Object.keys(this.modules)
    };
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADIntegration };
}
